import { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { LineSkeleton } from '../components/Skeleton';
import '../styles/MainLayout.css';

interface MainLayoutProps {
  onLock: () => void;
}

// Fallback shown while a lazily-loaded screen chunk is fetching (see the
// React.lazy screen imports in router.tsx). Scoped to the main content area
// so the persistent sidebar stays mounted during a cold-chunk navigation —
// only the page body shows the shimmer. Warm chunks resolve synchronously
// and never render this.
function ScreenLoadingFallback() {
  return (
    <div className="screen-lazy-fallback" style={{ padding: '2rem' }} aria-busy="true">
      <LineSkeleton width="30%" height="1.5em" style={{ marginBottom: '1.5rem' }} />
      <LineSkeleton width="100%" style={{ marginBottom: '0.75rem' }} />
      <LineSkeleton width="92%" style={{ marginBottom: '0.75rem' }} />
      <LineSkeleton width="78%" />
    </div>
  );
}

export default function MainLayout({ onLock }: MainLayoutProps) {
  const location = useLocation();

  return (
    <div className="main-layout">
      <Sidebar currentPath={location.pathname} onLock={onLock} />
      <main className="main-content">
        {/* Suspense boundary for the React.lazy route screens. Placed around
            the Outlet (not the whole router) so the sidebar persists and only
            the content region shows the loading fallback. */}
        <Suspense fallback={<ScreenLoadingFallback />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
